import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Location, RideRequestLocation, Sublocation } from '../../models';
import { IGeoCoords } from '../maps';

@Injectable({
  providedIn: 'root'
})
export class LocationService extends UzurvRestService {
  readonly resourceUrl = 'locations/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a specific location by Id
   *
   * @param id
   * @permission location_read
   */
  findLocationByID(id: number): Observable<Location> {
    return this.get<Location>(`${this.resourceUrl}${id}`).pipe(map(response => Deserialize(response, Location)));
  }

  /**
   * Returns a specific sublocation by Id
   *
   * @param id
   * @permission location_read
   */
  findSublocationByID(locationId: number, sublocationId: number): Observable<Location> {
    return this.get<Sublocation>(`${this.resourceUrl}${locationId}/sublocations/${sublocationId}`).pipe(
      map(response => Deserialize(response, Sublocation))
    );
  }

  /**
   * Used to create or look up a location. The backend will return an existing location (if it exists)
   * or create a new one and return it with the fields filled out. This should replace any need for front-end
   * geocoding
   *
   * @param display The display address of a location, e.g. from Google Maps Autocomplete
   * @permission location_create
   */
  addLocation(display: string): Observable<Location> {
    const location = new Location();
    location.display = display;
    return this.post<Location>(`${this.resourceUrl}`, Serialize(location)).pipe(map(response => Deserialize(response, Location)));
  }

  /**
   * Adds a new sublocation to a location
   *
   * @param locationId
   * @param sublocation
   * @permission location_management
   */
  addSublocation(locationId: number, sublocation: Sublocation): Observable<Sublocation> {
    return this.post<Sublocation>(`${this.resourceUrl}${locationId}/sublocations/`, Serialize(sublocation)).pipe(
      map(response => Deserialize(response, Sublocation))
    );
  }

  /**
   * Deletes a sublocation by Id. Note that the location doesn't matter,
   * sublocation IDs are globally unique
   *
   * @param sublocationId
   * @permission location_management
   */
  deleteSublocation(sublocationId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}sublocations/${sublocationId}`);
  }

  /**
   * Updates an existing location's latitude and longitude
   *
   * @param locationId
   * @param latitude
   * @param longitude
   * @permission location_management
   */
  patchLatitudeAndLongitudeForLocationWithId(locationId: number, latitude: number, longitude: number): Observable<Location> {
    const patchDictionary = {
      id: locationId,
      latitude: latitude,
      longitude: longitude
    };

    return this.patch(`${this.resourceUrl}${locationId}`, patchDictionary).pipe(map(response => Deserialize(response, Location)));
  }

  /**
   * Takes a Location and returns a RideRequestLocation
   *
   * @param location
   */
  locationToRideRequestLocation(location: Location, offsetCoords?: IGeoCoords): RideRequestLocation {
    if (isNullOrUndefined(location)) {
      return null;
    }

    const rideRequestLocation = new RideRequestLocation();
    rideRequestLocation.locationId = location.id;
    rideRequestLocation.display = location.display;
    rideRequestLocation.latitude = location.latitude;
    rideRequestLocation.longitude = location.longitude;

    // TODO (phill2mj): don't do sublocations like this. instead this method should consume a location and optional sublocation
    if (location.sublocations && location.sublocations[0]) {
      rideRequestLocation.sublocationId = location.sublocations[0].id;
      rideRequestLocation.sublocationName = location.sublocations[0].name;
      rideRequestLocation.sublocationDescription = location.sublocations[0].description;
      rideRequestLocation.latitude = location.sublocations[0].latitude;
      rideRequestLocation.longitude = location.sublocations[0].longitude;
    }
    if (offsetCoords) {
      rideRequestLocation.requiresReview = true;
      rideRequestLocation.latitude = +offsetCoords.lat.toFixed(6);
      rideRequestLocation.longitude = +offsetCoords.lng.toFixed(6);
      rideRequestLocation.sublocationId = null;
      rideRequestLocation.sublocationName = null;
      rideRequestLocation.sublocationDescription = null;
    }
    return rideRequestLocation;
  }

  getStreetAddressFromDisplay(display: string): string {
    if (!isNullOrUndefined(display)) {
      return display.substr(0, display.indexOf(','));
    } else {
      return;
    }
  }

  getCityStateFromDisplay(display: string): string {
    if (!isNullOrUndefined(display)) {
      return display.substr(display.indexOf(',') + 1, display.length - 1);
    } else {
      return;
    }
  }

  getCityStateZipFromDisplay(display: string): string {
    if (!isNullOrUndefined(display)) {
      const start: number = display.indexOf(',') + 1;
      const length: number = display.lastIndexOf(',') - display.indexOf(',') - 1;
      return display.substr(start, length);
    } else {
      return;
    }
  }

  getStreetAddressCityStateZipFromDisplay(display: string): string {
    if (!isNullOrUndefined(display)) {
      return display.substr(0, display.lastIndexOf(','));
    } else {
      return;
    }
  }
}
